import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type {
  PaymentIntent,
  SetupIntent,
  Stripe,
  StripeElementsOptions,
  StripePaymentElementChangeEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';

import {
  BillingDetails,
  BillingAddress,
  createSetupIntent,
  createSubscriptionIntent,
  createBankTransferInvoice,
  fetchSubscriptionPrices,
  resumeSubscriptionIntent,
  createCustomerPortalSession,
  previewSubscriptionSummary,
  SubscriptionPrice,
  PaymentMethodDomain,
  COUPON_CODE_INVALID_MESSAGE,
} from '../api/subscription';
import { useAuth } from '../contexts/AuthContext';
import { lookupPostalCode, PostalLookupError } from '../api/postalLookup';
import { normalizeDigits } from '../utils/normalizeDigits';
import type { SnackbarState } from '../types';
import { resolveStripePublishableKey } from '../utils/stripeConfig';

interface PriceOption {
  id: string;
  label: string;
  description?: string;
  priceDisplay?: string;
  amount?: number;
  monthlyEquivalent?: number;
  billingInterval?: { interval?: string; intervalCount?: number };
}

interface AmountSummary {
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  dueDate?: number;
}

const normalizeCurrencyAmount = (value?: number | null): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  const rounded = Math.round(value);
  return rounded >= 0 ? rounded : 0;
};

const mergeSummaryWithInvoice = (
  invoice: {
    total?: number | null;
    subtotal?: number | null;
    tax?: number | null;
    discount?: number | null;
    dueDate?: number | null;
  },
  fallback?: AmountSummary | null,
): AmountSummary | null => {
  const invoiceTotal = normalizeCurrencyAmount(invoice.total);
  const fallbackTotal = normalizeCurrencyAmount(fallback?.total);
  const hasInvoiceTotal = invoiceTotal !== undefined;
  const subtotal = hasInvoiceTotal
    ? normalizeCurrencyAmount(invoice.subtotal)
    : normalizeCurrencyAmount(invoice.subtotal) ?? normalizeCurrencyAmount(fallback?.subtotal);
  const tax = hasInvoiceTotal
    ? normalizeCurrencyAmount(invoice.tax)
    : normalizeCurrencyAmount(invoice.tax) ?? normalizeCurrencyAmount(fallback?.tax);
  const discount = hasInvoiceTotal
    ? normalizeCurrencyAmount(invoice.discount)
    : normalizeCurrencyAmount(invoice.discount) ?? normalizeCurrencyAmount(fallback?.discount);
  const total = invoiceTotal ?? fallbackTotal;
  const invoiceDueDate =
    typeof invoice.dueDate === 'number' && Number.isFinite(invoice.dueDate) && invoice.dueDate > 0
      ? Math.trunc(invoice.dueDate)
      : undefined;
  const fallbackDueDate =
    typeof fallback?.dueDate === 'number' && Number.isFinite(fallback.dueDate) && fallback.dueDate > 0
      ? fallback.dueDate
      : undefined;
  const dueDate = invoiceDueDate ?? fallbackDueDate;

  if (total === undefined && subtotal === undefined && discount === undefined && tax === undefined) {
    return fallback ?? null;
  }

  return {
    ...(total !== undefined ? { total } : {}),
    ...(subtotal !== undefined ? { subtotal } : {}),
    ...(tax !== undefined ? { tax } : {}),
    ...(dueDate !== undefined ? { dueDate } : {}),
    ...(discount !== undefined ? { discount } : {}),
  };
};

interface SubscriptionSuccessPayload {
  operationId: string;
}

interface PaymentConfirmationResult {
  operationId: string;
  setupIntentId?: string;
}

interface PreparedIntent {
  clientSecret?: string;
  operationId: string;
  intentType: 'payment' | 'setup' | 'invoice';
  subscriptionId: string;
  status?: string;
  statusMessage?: string;
  summary?: AmountSummary | null;
  paymentMethodTypes?: string[];
  paymentMethodDomain?: PaymentMethodDomain;
  hostedInvoiceUrl?: string;
  invoicePdfUrl?: string;
}


interface SubscriptionFormProps {
  priceOptions: PriceOption[];
  stripePromise: ReturnType<typeof loadStripe> | null;
  stripeReady: boolean;
  userEmail?: string;
  userName?: string;
  userHospitalName?: string;
  onSuccess: (payload: SubscriptionSuccessPayload) => void;
  onError: (message: string) => void;
  onFinalReviewVisibilityChange?: (visible: boolean) => void;
  applePayStatus: ApplePayStatus;
  subscriptionPeriodEnd?: number | null;
  replaceAtPeriodEnd?: boolean;
}

const prefectures = [
  '北海道',
  '青森県',
  '岩手県',
  '宮城県',
  '秋田県',
  '山形県',
  '福島県',
  '茨城県',
  '栃木県',
  '群馬県',
  '埼玉県',
  '千葉県',
  '東京都',
  '神奈川県',
  '新潟県',
  '富山県',
  '石川県',
  '福井県',
  '山梨県',
  '長野県',
  '岐阜県',
  '静岡県',
  '愛知県',
  '三重県',
  '滋賀県',
  '京都府',
  '大阪府',
  '兵庫県',
  '奈良県',
  '和歌山県',
  '鳥取県',
  '島根県',
  '岡山県',
  '広島県',
  '山口県',
  '徳島県',
  '香川県',
  '愛媛県',
  '高知県',
  '福岡県',
  '佐賀県',
  '長崎県',
  '熊本県',
  '大分県',
  '宮崎県',
  '鹿児島県',
  '沖縄県',
];

const CARDHOLDER_NAME_HELPER_TEXT = 'クレジットカードに記載された名義と同じ内容を入力してください。';
const CARDHOLDER_NAME_ERROR_TEXT =
  'クレジットカード名義人を入力してください。クレジットカードに記載された名義と同じ内容を入力してください。';

const parseYenAmount = (value?: string | number | null): number | undefined => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  const normalized = normalizeDigits(String(value));
  const sanitized = normalized.replace(/[^0-9.]/g, '');
  if (!sanitized) {
    return undefined;
  }
  const parsed = Number.parseFloat(sanitized);
  return Number.isFinite(parsed) ? Math.round(parsed) : undefined;
};

const formatYen = (amount?: number): string | undefined => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return undefined;
  }
  return `¥${amount.toLocaleString('ja-JP')}`;
};

const formatDueDate = (timestamp?: number): string | undefined => {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp <= 0) {
    return undefined;
  }

  const milliseconds = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
  const date = new Date(milliseconds);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  const dateFormatter = new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const weekdayFormatter = new Intl.DateTimeFormat('ja-JP', { weekday: 'short' });
  const dateLabel = dateFormatter.format(date);
  const weekdayLabel = weekdayFormatter.format(date);
  return `${dateLabel}（${weekdayLabel}）`;
};

const INVOICE_DUE_GRACE_DAYS = 7;
const MILLISECONDS_IN_SECOND = 1000;

const toDateFromTimestamp = (timestamp?: number): Date | undefined => {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp <= 0) {
    return undefined;
  }

  const milliseconds = timestamp < 1_000_000_000_000 ? timestamp * MILLISECONDS_IN_SECOND : timestamp;
  const parsed = new Date(milliseconds);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed;
};

const subtractGracePeriod = (date: Date): Date => {
  const result = new Date(date.getTime());
  result.setUTCDate(result.getUTCDate() - INVOICE_DUE_GRACE_DAYS);
  return result;
};

const addIntervalToDate = (
  date: Date,
  interval?: string | null,
  rawCount?: number | null,
): Date | undefined => {
  if (!interval) {
    return undefined;
  }

  const count = typeof rawCount === 'number' && rawCount > 0 ? Math.floor(rawCount) : 1;
  const result = new Date(date.getTime());

  switch (interval.toLowerCase()) {
    case 'day':
      result.setUTCDate(result.getUTCDate() + count);
      break;
    case 'week':
      result.setUTCDate(result.getUTCDate() + count * 7);
      break;
    case 'month':
      result.setUTCMonth(result.getUTCMonth() + count);
      break;
    case 'year':
      result.setUTCFullYear(result.getUTCFullYear() + count);
      break;
    default:
      return undefined;
  }

  return result;
};

const resolveNextBillingTimestamp = (
  options: {
    periodEnd?: number | null;
    summaryDueDate?: number | null;
    interval?: string | null;
    intervalCount?: number | null;
  },
): number | undefined => {
  if (typeof options.periodEnd === 'number' && Number.isFinite(options.periodEnd) && options.periodEnd > 0) {
    return options.periodEnd;
  }

  const dueDate = toDateFromTimestamp(options.summaryDueDate ?? undefined);
  if (!dueDate || !options.interval) {
    return undefined;
  }

  const serviceStart = subtractGracePeriod(dueDate);
  const estimatedPeriodEnd = addIntervalToDate(serviceStart, options.interval, options.intervalCount);
  if (!estimatedPeriodEnd) {
    return undefined;
  }

  return Math.floor(estimatedPeriodEnd.getTime() / MILLISECONDS_IN_SECOND);
};

const SERVICE_PROVISION_NOTICE =
  '銀行振込以外：決済処理後、直ちにご利用いただけます。\n銀行振込：お客様による振込手続き後、入金処理が完了次第（1~数営業日後）ご利用いただけます。';
const AUTO_RENEWAL_NOTICE =
  '本サービスは定期購入（サブスクリプション）です。お客様から解約の申し出がない限り、各請求期間の終了時に自動更新されます。銀行振込以外の場合、次回以降の請求額は、適用された割引の期間・条件により今回の請求額と異なる場合があります。契約内容と次回請求額をご確認ください。銀行振込の場合、当社から請求書を発行しますので、期日までにお振込みください。';
const SUBSCRIPTION_CANCELLATION_NOTICE =
  '・解約方法：Webアプリ画面上部の「ユーザーメニュー → サブスクリプション管理」からいつでも解約可能です。解約が完了すると、次回以降の自動課金は停止されます。なお、解約後も、既にお支払済みの請求期間（当該年／月）の終了日まではご利用いただけます。日割りでの返金や課金額調整は行いません。\n' +
  '・解約期限：次回請求日の前日までに上記の解約手続きを行なってください。';
const REFUND_POLICY_NOTICE =
  '本サービスはデジタルコンテンツの性質上、購入後の返金はいたしません。ただし、当社に起因する技術的不具合等により、合理的期間サービスを利用できなかった場合は、Stripe を通じた返金（全部または一部）や利用期間の延長等で誠実に対応いたします。返金等の申出は、当社までご利用者様の情報・状況の詳細を添えてご連絡ください。';
const CONTACT_INFORMATION_NOTICE =
  '合同会社ナースラボ\nお問い合わせ（メール）：support@nslab.co.jp';
const CONTACT_INFORMATION_FORM_URL = 'https://nslab.co.jp/inquiry/';
const STRIPE_PUBLISHABLE_KEY_MISSING_MESSAGE =
  'Stripe の公開鍵が設定されていないため、決済フォームを表示できません。管理者にお問い合わせください。';
const STRIPE_SCRIPT_BLOCKED_MESSAGE =
  'Stripe の読み込みに失敗しました。サーバーのセキュリティ設定またはネットワーク設定により、決済フォームがブロックされている可能性があります。管理者にお問い合わせください。';
const STRIPE_INITIALIZATION_FAILED_MESSAGE =
  '決済フォームを初期化できませんでした。時間をおいて再試行してください。';

const normalizePromotionCode = (value: string): string => {
  const normalized = value.normalize('NFKC').replace(/\s+/g, '').trim();
  if (!normalized) {
    return '';
  }

  const lowered = normalized.toLowerCase();
  if (lowered.startsWith('promo_') || lowered.startsWith('coupon_')) {
    return lowered;
  }

  return normalized.toUpperCase();
};

type PaymentMethodOrder = NonNullable<StripePaymentElementOptions['paymentMethodOrder']>;

const resolvePaymentMethodOrder = (
  paymentMethodTypes: string[] | undefined,
  shouldIncludeApplePay: boolean,
): PaymentMethodOrder | undefined => {
  if (!paymentMethodTypes?.length) {
    return undefined;
  }

  const normalized = paymentMethodTypes
    .map((type) => (typeof type === 'string' ? type.trim().toLowerCase() : ''))
    .map((type) => (type === 'customer_balance' ? 'bank_transfer' : type))
    .filter((type): type is string => Boolean(type));

  if (!normalized.length) {
    return undefined;
  }

  const seen = new Set<string>();
  const order: string[] = [];

  normalized.forEach((type) => {
    if (seen.has(type)) {
      return;
    }
    seen.add(type);

    if (type === 'card') {
      if (shouldIncludeApplePay) {
        order.push('apple_pay');
      }
      order.push('card');
      return;
    }

    if (type === 'link') {
      order.push('link');
      return;
    }

    if (type === 'apple_pay' || type === 'google_pay') {
      if (type === 'apple_pay' && !shouldIncludeApplePay) {
        return;
      }
      order.push(type);
      return;
    }

    order.push(type);
  });

  const uniqueOrder = order.filter((value, index) => order.indexOf(value) === index);

  return uniqueOrder.length ? (uniqueOrder as PaymentMethodOrder) : undefined;
};

const resolveWalletOptions = (
  paymentMethodTypes: string[] | undefined,
  shouldEnableApplePay: boolean,
): StripePaymentElementOptions['wallets'] | undefined => {
  if (!paymentMethodTypes?.some((type) => type?.trim().toLowerCase() === 'card')) {
    return undefined;
  }

  const applePayOption = shouldEnableApplePay ? 'auto' : 'never';

  return { applePay: applePayOption, googlePay: 'never' } as const;
};

interface ApplePayStatus {
  isSupported: boolean;
  canMakePayments: boolean | null;
}

const detectSafariLikeEnvironment = (nav: Navigator | undefined): boolean => {
  if (!nav) {
    return false;
  }

  const vendor = nav.vendor ?? '';
  if (!/apple/i.test(vendor)) {
    return false;
  }

  const userAgent = nav.userAgent ?? '';
  const lowered = userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(lowered);
  const isSafariEngine = /safari/.test(lowered);
  const isChromeLike = /crios|crmo|chrome|android/.test(lowered);
  const isEdgeLike = /edg/.test(lowered);
  const isOperaLike = /opera|opr\//.test(lowered);
  const isFirefoxIOS = /fxios/.test(lowered);

  if (isChromeLike || isEdgeLike || isOperaLike || isFirefoxIOS) {
    return false;
  }

  if (isIOS) {
    const iosNavigator = nav as Navigator & { standalone?: boolean };
    return isSafariEngine || iosNavigator.standalone === true;
  }

  return isSafariEngine;
};

const useApplePayStatus = (): ApplePayStatus => {
  const isSafari = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }
    return detectSafariLikeEnvironment(navigator);
  }, []);
  const isSupported = useMemo(
    () => typeof window !== 'undefined' && 'ApplePaySession' in window && isSafari,
    [isSafari],
  );
  const [canMakePayments, setCanMakePayments] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isSupported) {
      setCanMakePayments(false);
      return;
    }

    let isCancelled = false;
    setCanMakePayments(null);

    const applePayWindow = window as typeof window & {
      ApplePaySession?: { canMakePayments?: () => unknown };
    };
    const session = applePayWindow.ApplePaySession;

    const resolveResult = (value: unknown) => {
      if (isCancelled) {
        return;
      }
      if (typeof value === 'boolean') {
        setCanMakePayments(value);
        return;
      }
      if (value == null) {
        setCanMakePayments(true);
        return;
      }
      setCanMakePayments(Boolean(value));
    };

    if (!session || typeof session.canMakePayments !== 'function') {
      resolveResult(false);
      return () => {
        isCancelled = true;
      };
    }

    try {
      const result = session.canMakePayments();
      if (result && typeof (result as PromiseLike<unknown>).then === 'function') {
        Promise.resolve(result)
          .then((value) => {
            resolveResult(value);
          })
          .catch(() => {
            if (!isCancelled) {
              setCanMakePayments(false);
            }
          });
      } else {
        resolveResult(result);
      }
    } catch {
      if (!isCancelled) {
        setCanMakePayments(false);
      }
    }

    return () => {
      isCancelled = true;
    };
  }, [isSupported]);

  return { isSupported, canMakePayments };
};

const PENDING_PAYMENT_INTENT_STATUSES = new Set([
  'requires_action',
  'requires_confirmation',
  'processing',
  'requires_payment_intent',
  'requires_payment_method',
  'open',
]);

const PAYMENT_METHOD_TYPE_LABELS: Record<string, string> = {
  card: 'クレジットカード（申込み確定後ただちに決済）',
  apple_pay: 'Apple Pay',
  link: 'Link',
  google_pay: 'Google Pay',
};

const formatPaymentMethodType = (value: string): string =>
  value
    .split('_')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const resolvePaymentMethodLabel = (
  selectedType: string | null,
  availableTypes?: string[],
): string => {
  const candidates = [selectedType, ...(availableTypes ?? [])]
    .map((type) => type?.trim().toLowerCase() ?? '')
    .filter((type): type is string => Boolean(type));

  for (const type of candidates) {
    const label = PAYMENT_METHOD_TYPE_LABELS[type];
    if (label) {
      return label;
    }
  }

  const fallback = candidates[0];
  if (fallback) {
    return formatPaymentMethodType(fallback);
  }

  return PAYMENT_METHOD_TYPE_LABELS.card;
};

const FinalConfirmationDetails: React.FC<{
  planLabel?: string;
  paymentMethodLabel?: string | null;
  summary: AmountSummary | null;
  summaryError?: string | null;
  isLoadingSummary?: boolean;
  showDueDate?: boolean;
  nextPeriodEnd?: number | null;
  planInterval?: { interval?: string | null; intervalCount?: number | null };
}> = ({
  planLabel,
  paymentMethodLabel,
  summary,
  summaryError,
  isLoadingSummary,
  showDueDate = false,
  nextPeriodEnd,
  planInterval,
}) => {
  const totalLabel = formatYen(summary?.total);
  const dueDateLabel = formatDueDate(summary?.dueDate);
  const shouldShowDueDate = Boolean(showDueDate && dueDateLabel);
  const nextBillingTimestamp = resolveNextBillingTimestamp({
    periodEnd: nextPeriodEnd,
    summaryDueDate: summary?.dueDate,
    interval: planInterval?.interval ?? null,
    intervalCount: planInterval?.intervalCount ?? null,
  });
  const nextRenewalLabel = formatDueDate(nextBillingTimestamp);
  const shouldShowRenewalNotice = Boolean(nextRenewalLabel);

  let priceContent: React.ReactNode;
  if (isLoadingSummary) {
    priceContent = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">金額を計算しています…</Typography>
      </Box>
    );
  } else if (summaryError) {
    priceContent = <Alert severity="warning">{summaryError}</Alert>;
  } else if (summary && typeof summary.total === 'number') {
    const subtotalLabel = formatYen(summary.subtotal);
    const discountLabel = formatYen(summary.discount);
    const taxLabel = formatYen(summary.tax);
    priceContent = (
      <Stack spacing={0.5}>
        {typeof summary.subtotal === 'number' && (
          <Typography variant="body1">小計: {subtotalLabel}</Typography>
        )}
        {typeof summary.discount === 'number' && summary.discount > 0 && (
          <Typography variant="body1">割引: -{discountLabel}</Typography>
        )}
        {typeof summary.tax === 'number' && (
          <Typography variant="body1">税額: {taxLabel}</Typography>
        )}
        <Typography variant="h6" fontWeight="bold">
          合計: {totalLabel}
        </Typography>
      </Stack>
    );
  } else {
    priceContent = (
      <Typography variant="body2" color="text.secondary">
        金額情報を取得できませんでした。前の画面に戻り再度お試しください。
      </Typography>
    );
  }

  const resolvedPaymentMethodLabel = paymentMethodLabel?.trim();
  const shouldShowBankTransferNote = resolvedPaymentMethodLabel === '銀行振込';

  return (
    <Stack spacing={3}>
      {planLabel && (
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            選択中のプラン
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5 }}>
            {planLabel}
          </Typography>
        </Box>
      )}
      {resolvedPaymentMethodLabel ? (
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            選択中のお支払い方法
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5 }}>
            {resolvedPaymentMethodLabel}
          </Typography>
        </Box>
      ) : null}
      <Box data-testid="final-confirmation-total">
        <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
          お支払い金額
        </Typography>
        <Box sx={{ mt: 1 }}>{priceContent}</Box>
        {shouldShowBankTransferNote ? (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            振込手数料はお客様負担となります。
          </Typography>
        ) : null}
        {!shouldShowDueDate && shouldShowRenewalNotice ? (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
            次回請求日：{nextRenewalLabel}
          </Typography>
        ) : null}
        {shouldShowDueDate ? (
          <Box sx={{ mt: 1.5 }} data-testid="final-confirmation-due-date">
            <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
              お支払い期日
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {dueDateLabel}
            </Typography>
            {shouldShowRenewalNotice ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                次回請求日：{nextRenewalLabel}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Divider />
      <Stack spacing={2.5}>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: 'left', fontWeight: 'bold', color: 'text.primary' }}
          >
            サービスの提供時期
          </Typography>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.8, mt: 0.5, whiteSpace: 'pre-line', textAlign: 'left' }}
          >
            {SERVICE_PROVISION_NOTICE}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: 'left', fontWeight: 'bold', color: 'text.primary' }}
          >
            自動更新について
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, mt: 0.5, textAlign: 'left' }}>
            {AUTO_RENEWAL_NOTICE}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: 'left', fontWeight: 'bold', color: 'text.primary' }}
          >
            返金・返品について
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, mt: 0.5, textAlign: 'left' }}>
            {REFUND_POLICY_NOTICE}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: 'left', fontWeight: 'bold', color: 'text.primary' }}
          >
            サブスクリプションの解約について
          </Typography>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.8, mt: 0.5, whiteSpace: 'pre-line', textAlign: 'left' }}
          >
            {SUBSCRIPTION_CANCELLATION_NOTICE}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: 'left', fontWeight: 'bold', color: 'text.primary' }}
          >
            お問い合わせ先
          </Typography>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.8, mt: 0.5, whiteSpace: 'pre-line', textAlign: 'left' }}
          >
            {CONTACT_INFORMATION_NOTICE}
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.8, textAlign: 'left' }}>
            お問い合わせフォーム：{' '}
            <Link href={CONTACT_INFORMATION_FORM_URL} target="_blank" rel="noopener noreferrer">
              {CONTACT_INFORMATION_FORM_URL}
            </Link>
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

const TermsAgreementSection: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  showError: boolean;
}> = ({ checked, onChange, showError }) => (
  <Stack spacing={0.5} alignItems="center">
    <FormControl error={showError} sx={{ width: '100%', alignItems: 'center' }}>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={checked}
            onChange={(event) => {
              onChange(event.target.checked);
            }}
          />
        }
        label={
          <Typography variant="body2" component="span">
            <Link
              component={RouterLink}
              to="https://nslab.co.jp/services/ict-data-analysis/terms-of-use/"
              target="_blank"
              rel="noopener"
            >
              利用規約
            </Link>
            と
            <Link
              component={RouterLink}
              to="https://nslab.co.jp/services/ict-data-analysis/privacy_policy/"
              target="_blank"
              rel="noopener"
            >
              プライバシーポリシー
            </Link>
            に同意します
          </Typography>
        }
        sx={{
          mx: 'auto',
          alignItems: 'center',
          '& .MuiTypography-root': {
            textAlign: 'center',
          },
        }}
      />
      {showError ? <FormHelperText sx={{ textAlign: 'center' }}>同意が必要です。</FormHelperText> : null}
    </FormControl>
    <Typography variant="body2" sx={{ textAlign: 'center' }}>
      <Link
        href="https://nslab.co.jp/services/ict-data-analysis/legal_disclosure/"
        target="_blank"
        rel="noopener"
      >
        特定商取引法に基づく表記
      </Link>
    </Typography>
  </Stack>
);

const formatIntervalDescription = (price?: SubscriptionPrice): string | undefined => {
  const interval = price?.recurring?.interval?.toLowerCase();
  if (!interval) {
    return undefined;
  }

  const rawCount = price?.recurring?.intervalCount;
  const count = typeof rawCount === 'number' && rawCount > 0 ? rawCount : 1;

  let unit: string | undefined;
  switch (interval) {
    case 'day':
      unit = '日';
      break;
    case 'week':
      unit = '週間';
      break;
    case 'month':
      unit = 'ヶ月';
      break;
    case 'year':
      unit = '年';
      break;
    default:
      unit = undefined;
  }

  if (!unit) {
    return undefined;
  }

  const countLabel = count === 1 ? `1${unit}` : `${count}${unit}`;
  return `${countLabel}ごとの自動更新`;
};

const appendIntervalToLabel = (baseLabel: string, price?: SubscriptionPrice): string => {
  const trimmed = baseLabel.trim();
  const intervalDescription = formatIntervalDescription(price);
  if (!intervalDescription) {
    return trimmed;
  }

  return trimmed.includes(intervalDescription) ? trimmed : `${trimmed}（${intervalDescription}）`;
};

const formatPriceLabel = (amount?: number): string | undefined => formatYen(amount);

const mapPricesToOptions = (prices: SubscriptionPrice[]): PriceOption[] => {
  const options: PriceOption[] = [];
  const handledIds = new Set<string>();

  const resolveAmount = (price?: SubscriptionPrice): number | undefined => {
    if (!price) {
      return undefined;
    }
    if (typeof price.unitAmount === 'number') {
      return price.unitAmount;
    }
    if (price.unitAmountDecimal) {
      return parseYenAmount(price.unitAmountDecimal);
    }
    const metadataAmount = price.metadata?.unitAmount
      ?? price.metadata?.unit_amount
      ?? price.metadata?.amount
      ?? price.metadata?.display_amount;
    return parseYenAmount(metadataAmount);
  };

  const isInterval = (price: SubscriptionPrice | undefined, interval: string) => {
    if (!price?.recurring) {
      return false;
    }
    const intervalCount = price.recurring.intervalCount ?? 1;
    return price.recurring.interval === interval && intervalCount === 1;
  };

  const monthlyPrice = prices.find((price) => isInterval(price, 'month'));
  const yearlyPrice = prices.find((price) => isInterval(price, 'year'));

  if (monthlyPrice) {
    const amount = resolveAmount(monthlyPrice);
    options.push({
      id: monthlyPrice.id,
      label: appendIntervalToLabel('月額プラン', monthlyPrice),
      description: '毎月のお支払いでご利用いただけます。',
      priceDisplay: formatPriceLabel(amount),
      amount,
      billingInterval: monthlyPrice.recurring
        ? {
            interval: monthlyPrice.recurring.interval ?? undefined,
            intervalCount: monthlyPrice.recurring.intervalCount ?? undefined,
          }
        : undefined,
    });
    handledIds.add(monthlyPrice.id);
  }

  if (yearlyPrice) {
    const amount = resolveAmount(yearlyPrice);
    options.push({
      id: yearlyPrice.id,
      label: appendIntervalToLabel('年額プラン', yearlyPrice),
      description: '1年分をまとめてお支払いいただくプランです。月額プラン比で2ヶ月分お得です。',
      priceDisplay: formatPriceLabel(amount),
      amount,
      monthlyEquivalent: typeof amount === 'number' ? Math.round(amount / 12) : undefined,
      billingInterval: yearlyPrice.recurring
        ? {
            interval: yearlyPrice.recurring.interval ?? undefined,
            intervalCount: yearlyPrice.recurring.intervalCount ?? undefined,
          }
        : undefined,
    });
    handledIds.add(yearlyPrice.id);
  }

  prices.forEach((price) => {
    if (!price || handledIds.has(price.id)) {
      return;
    }
    const amount = resolveAmount(price);
    const fallbackBaseLabel =
      price.nickname ?? price.product?.name ?? (price.recurring?.interval ? `${price.recurring.interval}プラン` : 'プラン');
    options.push({
      id: price.id,
      label: appendIntervalToLabel(fallbackBaseLabel, price),
      description: undefined,
      priceDisplay: formatPriceLabel(amount),
      amount,
      billingInterval: price.recurring
        ? {
            interval: price.recurring.interval ?? undefined,
            intervalCount: price.recurring.intervalCount ?? undefined,
          }
        : undefined,
    });
  });

  return options;
};

const buildBillingDetails = (
  email?: string,
  name?: string,
  phone?: string,
  address?: BillingAddress,
): BillingDetails => ({
  email,
  name,
  phone,
  address,
});

const mapBillingDetailsForStripe = (details: BillingDetails): Record<string, unknown> => {
  const billing: Record<string, unknown> = {};
  if (details.email) {
    billing.email = details.email;
  }
  if (details.name) {
    billing.name = details.name;
  }
  if (details.phone) {
    billing.phone = details.phone;
  }
  if (details.address) {
    const { line1, line2, city, state, postalCode, country } = details.address;
    billing.address = {
      ...(line1 ? { line1 } : {}),
      ...(line2 ? { line2 } : {}),
      ...(city ? { city } : {}),
      ...(state ? { state } : {}),
      ...(postalCode ? { postal_code: postalCode } : {}),
      ...(country ? { country } : {}),
    };
  }
  return billing;
};

const PaymentConfirmationStep: React.FC<{
  intent: PreparedIntent;
  billingDetails: BillingDetails;
  cardholderName: string;
  onCardholderNameChange: (value: string) => void;
  cardholderError?: string;
  onCardholderErrorChange: (message: string | null) => void;
  onBack: () => void;
  onSuccess: (result: PaymentConfirmationResult) => void;
  onError: (message: string) => void;
  setFormError: (message: string | null) => void;
  isConfirming: boolean;
  setIsConfirming: (value: boolean) => void;
  summary: AmountSummary | null;
  summaryError?: string | null;
  isLoadingSummary: boolean;
  planLabel?: string;
  onReviewVisibilityChange?: (visible: boolean) => void;
  applePayStatus: ApplePayStatus;
  isTermsAccepted: boolean;
  onTermsChange: (checked: boolean) => void;
  termsError: boolean;
  onTermsErrorChange: (value: boolean) => void;
  subscriptionPeriodEnd?: number | null;
  planInterval?: { interval?: string | null; intervalCount?: number | null };
}> = ({
  intent,
  billingDetails,
  cardholderName,
  onCardholderNameChange,
  cardholderError,
  onCardholderErrorChange,
  onBack,
  onSuccess,
  onError,
  setFormError,
  isConfirming,
  setIsConfirming,
  summary,
  summaryError,
  isLoadingSummary,
  planLabel,
  onReviewVisibilityChange,
  applePayStatus,
  isTermsAccepted,
  onTermsChange,
  termsError,
  onTermsErrorChange,
  subscriptionPeriodEnd,
  planInterval,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isReviewVisible, setIsReviewVisible] = useState(false);
  const [isPreparingReview, setIsPreparingReview] = useState(false);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<string | null>(null);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const { isSupported: isApplePaySupported, canMakePayments: canMakeApplePayPayments } = applePayStatus;
  const isApplePayAvailable = isApplePaySupported && canMakeApplePayPayments !== false;
  const shouldCollectCardholderName = (selectedPaymentMethodType ?? 'card') !== 'apple_pay';

  const effectiveBillingDetails = useMemo(() => {
    if (shouldCollectCardholderName) {
      return billingDetails;
    }
    return { ...billingDetails, name: undefined };
  }, [billingDetails, shouldCollectCardholderName]);

  useEffect(() => {
    setIsReviewVisible(false);
    setIsPreparingReview(false);
    setSelectedPaymentMethodType(null);
    setIsPaymentElementReady(false);
  }, [intent.clientSecret]);

  useEffect(() => {
    onReviewVisibilityChange?.(isReviewVisible);
  }, [isReviewVisible, onReviewVisibilityChange]);

  useEffect(() => {
    if (!isReviewVisible) {
      onTermsErrorChange(false);
    }
  }, [isReviewVisible, onTermsErrorChange]);

  useEffect(
    () => () => {
      onReviewVisibilityChange?.(false);
    },
    [onReviewVisibilityChange],
  );

  useEffect(() => {
    if (!shouldCollectCardholderName) {
      onCardholderErrorChange(null);
      setFormError(null);
    }
  }, [onCardholderErrorChange, setFormError, shouldCollectCardholderName]);

  const paymentElementOptions = useMemo<StripePaymentElementOptions>(() => {
    const email = billingDetails.email?.trim();
    const base: StripePaymentElementOptions = email
      ? {
          layout: 'tabs',
          defaultValues: {
            billingDetails: {
              email,
            },
          },
        }
      : { layout: 'tabs' };

    base.fields = {
      billingDetails: {
        address: 'never',
      },
    };

    const paymentMethodOrder = resolvePaymentMethodOrder(
      intent.paymentMethodTypes,
      isApplePayAvailable,
    );
    if (paymentMethodOrder) {
      base.paymentMethodOrder = paymentMethodOrder;
    }

    const wallets = resolveWalletOptions(intent.paymentMethodTypes, isApplePayAvailable);
    if (wallets) {
      base.wallets = wallets;
    }

    return base;
  }, [billingDetails.email, intent.paymentMethodTypes, isApplePayAvailable]);

  const paymentDomain = intent.paymentMethodDomain;
  const paymentDomainMessage = useMemo(() => {
    if (!isApplePaySupported || !paymentDomain?.domain) {
      return null;
    }
    const statusHint = paymentDomain.verificationStatus
      ? `検証ステータス: ${paymentDomain.verificationStatus}`
      : null;
    if (canMakeApplePayPayments === false) {
      return [
        'この環境ではApple Payは使用できません。',
        statusHint ? `(${statusHint})` : null,
      ]
        .filter(Boolean)
        .join(' ');
    }
    if (!paymentDomain.isVerified) {
      return null;
    }
    return [
      `Apple Payの登録ドメイン「${paymentDomain.domain}」はStripe上で確認済みです。`,
      statusHint ? `(${statusHint})` : null,
    ]
      .filter(Boolean)
      .join(' ');
  }, [canMakeApplePayPayments, isApplePaySupported, paymentDomain]);

  const handleConfirm = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const normalizedCardholder = effectiveBillingDetails.name?.trim() ?? '';
      if (shouldCollectCardholderName && !normalizedCardholder) {
        onCardholderErrorChange(CARDHOLDER_NAME_ERROR_TEXT);
        setFormError(CARDHOLDER_NAME_ERROR_TEXT);
        return;
      }

      if (!stripe || !elements) {
        const message = '決済フォームの初期化に失敗しました。時間をおいて再度お試しください。';
        setFormError(message);
        onError(message);
        return;
      }

      if (!isReviewVisible) {
        setIsPreparingReview(true);
        setFormError(null);
        onCardholderErrorChange(null);
        onTermsErrorChange(false);

        const { error: submitError } = await elements.submit();
        setIsPreparingReview(false);
        if (submitError) {
          const message = submitError.message ?? '決済情報の入力内容を確認してください。';
          setFormError(message);
          onError(message);
          return;
        }

        setIsReviewVisible(true);
        return;
      }

      if (!isTermsAccepted) {
        onTermsErrorChange(true);
        return;
      }

      onTermsErrorChange(false);
      setIsConfirming(true);
      setFormError(null);
      onCardholderErrorChange(null);

      try {
        const successUrl = new URL('/subscription/success', window.location.origin);
        successUrl.searchParams.set('operation_id', intent.operationId);
        const confirmParams = {
          elements,
          clientSecret: intent.clientSecret,
          redirect: 'if_required',
          confirmParams: {
            return_url: successUrl.toString(),
            payment_method_data: {
              billing_details: mapBillingDetailsForStripe(effectiveBillingDetails),
            },
          },
        } as const;

        if (intent.intentType === 'setup') {
          const setupResult = await stripe.confirmSetup(confirmParams);
          if (setupResult.error) {
            const message =
              setupResult.error.message ?? '決済情報の保存に失敗しました。別のクレジットカードでお試しください。';
            setFormError(message);
            onError(message);
            setIsConfirming(false);
            return;
          }
          const { setupIntent } = setupResult as { setupIntent?: SetupIntent };
          if (!setupIntent) {
            const message = '決済情報の確認に失敗しました。時間をおいて再度お試しください。';
            setFormError(message);
            onError(message);
            setIsConfirming(false);
            return;
          }
          onSuccess({
            operationId: intent.operationId,
            setupIntentId: setupIntent.id,
          });
        } else {
          const paymentResult = await stripe.confirmPayment(confirmParams);
          if (paymentResult.error) {
            const message = paymentResult.error.message ?? '決済に失敗しました。別のクレジットカードでお試しください。';
            setFormError(message);
            onError(message);
            setIsConfirming(false);
            return;
          }
          const { paymentIntent } = paymentResult as { paymentIntent?: PaymentIntent };
          if (!paymentIntent) {
            const message = '決済情報の確認に失敗しました。時間をおいて再度お試しください。';
            setFormError(message);
            onError(message);
            setIsConfirming(false);
            return;
          }
          onSuccess({
            operationId: intent.operationId,
          });
        }
      } catch {
        const message =
          '決済処理中にエラーが発生しました。時間をおいて再度お試しください。';
        setFormError(message);
        onError(message);
        setIsConfirming(false);
        return;
      }

      setIsConfirming(false);
    },
    [
      effectiveBillingDetails,
      elements,
      isReviewVisible,
      intent,
      isTermsAccepted,
      onCardholderErrorChange,
      onError,
      onSuccess,
      onTermsErrorChange,
      setFormError,
      setIsConfirming,
      stripe,
      shouldCollectCardholderName,
    ],
  );

  const handleBackToCardInput = useCallback(() => {
    setIsReviewVisible(false);
    setFormError(null);
  }, [setFormError]);

  const handleChangeMethod = useCallback(() => {
    setIsReviewVisible(false);
    onBack();
  }, [onBack]);

  const reviewSummary = intent.summary ?? summary ?? null;
  const paymentMethodLabel = useMemo(
    () => resolvePaymentMethodLabel(selectedPaymentMethodType, intent.paymentMethodTypes),
    [intent.paymentMethodTypes, selectedPaymentMethodType],
  );
  const isActionDisabled = isPreparingReview || isConfirming;
  const isPaymentElementUnavailable = !isPaymentElementReady;
  const shouldShowPaymentElementStatus = !isReviewVisible && !isPaymentElementReady;

  return (
    <Box component="form" onSubmit={handleConfirm} sx={{ mt: 3 }}>
      <Stack spacing={2}>
        <Typography
          variant={isReviewVisible ? 'h5' : 'h6'}
          sx={{ fontWeight: isReviewVisible ? 'bold' : undefined }}
        >
          {isReviewVisible ? '契約内容最終確認' : '決済情報の入力'}
        </Typography>
        <Box sx={{ display: isReviewVisible ? 'none' : 'block' }}>
          <Stack spacing={2}>
            {paymentDomainMessage && (
              <Alert severity={paymentDomain?.isVerified === false ? 'warning' : 'info'}>
                {paymentDomainMessage}
              </Alert>
            )}
            {shouldCollectCardholderName && (
              <TextField
                label="クレジットカード名義人"
                value={cardholderName}
                onChange={(event) => {
                  onCardholderNameChange(event.target.value);
                }}
                fullWidth
                required
                error={Boolean(cardholderError)}
                helperText={cardholderError ?? CARDHOLDER_NAME_HELPER_TEXT}
                inputProps={{ autoComplete: 'cc-name' }}
                disabled={isActionDisabled}
              />
            )}
            <PaymentElement
              options={paymentElementOptions}
              onReady={() => {
                setIsPaymentElementReady(true);
              }}
              onChange={(event: StripePaymentElementChangeEvent) => {
                const nextType = event?.value?.type ?? null;
                setSelectedPaymentMethodType(nextType);
              }}
            />
            {shouldShowPaymentElementStatus && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  決済フォームを準備しています。しばらくお待ちください。
                </Typography>
              </Stack>
            )}
            {intent.statusMessage && intent.status !== 'requires_payment_method' && (
              <Alert severity="info">{intent.statusMessage}</Alert>
            )}
          </Stack>
        </Box>
        <Box sx={{ display: isReviewVisible ? 'block' : 'none' }}>
          <Stack spacing={2}>
          <FinalConfirmationDetails
            planLabel={planLabel}
            paymentMethodLabel={paymentMethodLabel}
            summary={reviewSummary}
            summaryError={summaryError}
            isLoadingSummary={isLoadingSummary}
            showDueDate={false}
            nextPeriodEnd={
              typeof subscriptionPeriodEnd === 'number' ? subscriptionPeriodEnd : undefined
            }
            planInterval={planInterval}
          />
            {intent.statusMessage && intent.status !== 'requires_payment_method' && (
              <Alert severity="info">{intent.statusMessage}</Alert>
            )}
          </Stack>
        </Box>
        {!isReviewVisible ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <Button variant="outlined" color="primary" onClick={onBack} disabled={isActionDisabled}>
              前のページに戻る
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isActionDisabled || isPaymentElementUnavailable}
            >
              {isPreparingReview ? '処理中…' : '最終確認へ進む'}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TermsAgreementSection
              checked={isTermsAccepted}
              onChange={onTermsChange}
              showError={termsError}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                width: '100%',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: { sm: 'space-between' },
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBackToCardInput}
                  disabled={isActionDisabled}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                >
                  前のページに戻る
                </Button>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleChangeMethod}
                  disabled={isConfirming}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                >
                  決済方法を変更する
                </Button>
              </Stack>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isConfirming || isPaymentElementUnavailable}
                sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
              >
                {isConfirming ? '処理中…' : '支払いを確定する'}
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const InvoicePaymentStep: React.FC<{
  intent: PreparedIntent;
  onBack: () => void;
}> = ({ intent, onBack }) => {
  const hostedInvoiceUrl = intent.hostedInvoiceUrl;
  const dueDateLabel = formatDueDate(intent.summary?.dueDate);

  return (
    <Stack spacing={3}>
      <Typography variant="h6">銀行振込でのお支払い</Typography>
      {intent.statusMessage && <Alert severity="info">{intent.statusMessage}</Alert>}
      {dueDateLabel ? (
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            お支払い期日
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            {dueDateLabel}
          </Typography>
        </Box>
      ) : null}
      {hostedInvoiceUrl ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="center"
        >
          <Button
            component="a"
            variant="contained"
            color="primary"
            href={hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: 220 }}
          >
            請求書ページを開く
          </Button>
          {intent.invoicePdfUrl ? (
            <Button
              component="a"
              variant="outlined"
              color="primary"
              href={intent.invoicePdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ minWidth: 220 }}
            >
              請求書をダウンロード
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Alert severity="error">請求書のURLを取得できませんでした。時間をおいて再試行してください。</Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
        請求書ページに記載された振込先（仮想口座）へお支払いください。
        お支払い完了後、入金確認（1〜数営業日）後自動的にサブスクリプションが有効化されます。
        サブスクリプションが有効化されましたら、一度ログアウトしていただき、再度ログインしていただくことでサービスをご利用になれます。
        なお、お支払い状況につきましては、「ユーザーメニュー」の「サブスクリプション管理」でご確認いただけます。
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button variant="outlined" color="primary" onClick={onBack} sx={{ minWidth: 200 }}>
          前のページに戻る
        </Button>
      </Stack>
    </Stack>
  );
};

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  priceOptions,
  stripePromise,
  stripeReady,
  userEmail,
  userName,
  userHospitalName,
  onSuccess,
  onError,
  onFinalReviewVisibilityChange,
  applePayStatus,
  subscriptionPeriodEnd,
  replaceAtPeriodEnd = false,
}) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [selectedPriceId, setSelectedPriceId] = useState<string>(priceOptions[0]?.id ?? '');
  const [organizationName, setOrganizationName] = useState<string>(userHospitalName ?? '');
  const [hasOrganizationNameBeenEdited, setHasOrganizationNameBeenEdited] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [prefecture, setPrefecture] = useState('');
  const [city, setCity] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [promotionCode, setPromotionCode] = useState('');
  const [isPromotionCodeFieldVisible, setIsPromotionCodeFieldVisible] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [intentInfo, setIntentInfo] = useState<PreparedIntent | null>(null);
  const [elementsKey, setElementsKey] = useState(0);
  const [summary, setSummary] = useState<AmountSummary | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isMethodSelectionVisible, setIsMethodSelectionVisible] = useState(false);
  const [isBankReviewVisible, setIsBankReviewVisible] = useState(false);
  const [isCardReviewVisible, setIsCardReviewVisible] = useState(false);
  const [isReplacementAccepted, setIsReplacementAccepted] = useState(false);
  const isApplePayAvailable = applePayStatus.isSupported && applePayStatus.canMakePayments !== false;
  const [preparedBillingDetails, setPreparedBillingDetails] = useState<BillingDetails | null>(null);
  const [preparedPromotionCode, setPreparedPromotionCode] = useState<string | undefined>(undefined);
  const [isPostalLookupLoading, setIsPostalLookupLoading] = useState(false);
  const [postalLookupMessage, setPostalLookupMessage] = useState<string | null>(null);
  const [manualAddressOverrides, setManualAddressOverrides] = useState({
    prefecture: false,
    city: false,
    addressLine1: false,
  });
  const manualOverridesRef = useRef(manualAddressOverrides);
  const prefectureRef = useRef(prefecture);
  const cityRef = useRef(city);
  const addressLine1Ref = useRef(addressLine1);
  const appliedPromotionCodeRef = useRef('');
  const normalizedPromotionCode = useMemo(() => normalizePromotionCode(promotionCode), [promotionCode]);
  const [appliedPromotionCode, setAppliedPromotionCode] = useState(normalizedPromotionCode);

  const handleTermsChange = useCallback((checked: boolean) => {
    setIsTermsAccepted(checked);
    if (checked) {
      setTermsError(false);
    }
  }, []);

  useEffect(() => {
    manualOverridesRef.current = manualAddressOverrides;
  }, [manualAddressOverrides]);

  useEffect(() => {
    prefectureRef.current = prefecture;
  }, [prefecture]);

  useEffect(() => {
    cityRef.current = city;
  }, [city]);

  useEffect(() => {
    addressLine1Ref.current = addressLine1;
  }, [addressLine1]);

  useEffect(() => {
    appliedPromotionCodeRef.current = appliedPromotionCode;
  }, [appliedPromotionCode]);

  useEffect(() => {
    const normalized = normalizeDigits(postalCode.trim());
    const digits = normalized.replace(/[^0-9]/g, '');

    if (digits.length !== 7) {
      setIsPostalLookupLoading(false);
      setPostalLookupMessage(null);
      return () => {
        /* no cleanup needed */
      };
    }

    const controller = new AbortController();
    let isActive = true;

    setIsPostalLookupLoading(true);
    setPostalLookupMessage('郵便番号から住所を検索しています…');
    setFieldErrors((prev) => {
      if (!prev.postalCode) {
        return prev;
      }
      const next = { ...prev };
      delete next.postalCode;
      return next;
    });

    const fetchAddress = async () => {
      try {
        const result = await lookupPostalCode(digits, { signal: controller.signal });
        if (!isActive) {
          return;
        }
        if (!result) {
          setPostalLookupMessage('該当する住所が見つかりませんでした。住所を直接入力してください。');
          return;
        }

        const overrides = manualOverridesRef.current;
        const updates: Partial<typeof manualAddressOverrides> = {};
        let applied = false;

        if ((!overrides.prefecture || !prefectureRef.current.trim()) && result.prefecture) {
          setPrefecture(result.prefecture);
          updates.prefecture = false;
          applied = true;
        }
        if ((!overrides.city || !cityRef.current.trim()) && result.city) {
          setCity(result.city);
          updates.city = false;
          applied = true;
        }
        if ((!overrides.addressLine1 || !addressLine1Ref.current.trim()) && result.addressLine1) {
          setAddressLine1(result.addressLine1);
          updates.addressLine1 = false;
          applied = true;
        }

        if (Object.keys(updates).length > 0) {
          setManualAddressOverrides((prev) => {
            const next = { ...prev, ...updates };
            if (
              next.prefecture === prev.prefecture &&
              next.city === prev.city &&
              next.addressLine1 === prev.addressLine1
            ) {
              return prev;
            }
            return next;
          });
        }

        if (applied) {
          setPostalLookupMessage('郵便番号から住所を自動入力しました。');
          setFieldErrors((prev) => {
            const next = { ...prev };
            delete next.prefecture;
            delete next.city;
            delete next.addressLine1;
            return next;
          });
        } else {
          setPostalLookupMessage('住所情報を取得しました。既存の入力は変更していません。');
        }
      } catch (error) {
        if (!isActive) {
          return;
        }
        const maybeAxiosError = error as { code?: string; name?: string };
        if (maybeAxiosError?.code === 'ERR_CANCELED' || maybeAxiosError?.name === 'CanceledError') {
          return;
        }
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        if (error instanceof PostalLookupError) {
          setPostalLookupMessage(error.message || '住所検索に失敗しました。住所を直接入力してください。');
          return;
        }
        setPostalLookupMessage('住所検索に失敗しました。住所を直接入力してください。');
      } finally {
        if (isActive) {
          setIsPostalLookupLoading(false);
        }
      }
    };

    void fetchAddress();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [postalCode]);

  useEffect(() => {
    if (!hasOrganizationNameBeenEdited) {
      setOrganizationName(userHospitalName ?? '');
    }
  }, [hasOrganizationNameBeenEdited, userHospitalName]);

  useEffect(() => {
    let isCancelled = false;
    if (!selectedPriceId) {
      setSummary(null);
      return () => {
        isCancelled = true;
      };
    }

    const fetchSummary = async () => {
      const requestPromotionCode = appliedPromotionCode || undefined;
      setIsLoadingSummary(true);
      setSummaryError(null);
      try {
        const result = await previewSubscriptionSummary(selectedPriceId, {
          promotionCode: requestPromotionCode,
        });
        if (isCancelled) {
          return;
        }
        if (appliedPromotionCodeRef.current !== (requestPromotionCode ?? '')) {
          return;
        }
        setSummary({
          subtotal: result.subtotal,
          discount: result.discount,
          tax: result.tax,
          total: result.total,
          dueDate: result.dueDate,
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }
        if (appliedPromotionCodeRef.current !== (requestPromotionCode ?? '')) {
          return;
        }
        const message = error instanceof Error ? error.message : '';
        setSummaryError(
          message === COUPON_CODE_INVALID_MESSAGE
            ? message
            : '金額を事前確認できませんでした。合計はStripe確認画面で確定します。',
        );
      } finally {
        if (!isCancelled) {
          setIsLoadingSummary(false);
        }
      }
    };

    void fetchSummary();

    return () => {
      isCancelled = true;
    };
  }, [appliedPromotionCode, selectedPriceId]);

  const selectedPlan = useMemo(
    () => priceOptions.find((option) => option.id === selectedPriceId),
    [priceOptions, selectedPriceId],
  );

  const resetIntentState = useCallback(() => {
    setIntentInfo(null);
    setElementsKey((prev) => prev + 1);
    setIsConfirming(false);
    setStatusMessage(null);
    setIsMethodSelectionVisible(false);
    setPreparedBillingDetails(null);
    setPreparedPromotionCode(undefined);
    setIsBankReviewVisible(false);
    setTermsError(false);
  }, []);

  const backToMethodSelection = useCallback(() => {
    setIntentInfo(null);
    setElementsKey((prev) => prev + 1);
    setIsConfirming(false);
    setStatusMessage(null);
    setIsMethodSelectionVisible(true);
    setIsBankReviewVisible(false);
    setTermsError(false);
  }, []);

  const handleCardholderNameChange = useCallback((value: string) => {
    setCardholderName(value);
    setFieldErrors((prev) => {
      if (!prev.cardholderName) {
        return prev;
      }
      const next = { ...prev };
      delete next.cardholderName;
      return next;
    });
    setFormError((prev) => (prev === CARDHOLDER_NAME_ERROR_TEXT ? null : prev));
  }, []);

  const handleCardholderErrorChange = useCallback((message: string | null) => {
    setFieldErrors((prev) => {
      if (!message) {
        if (!prev.cardholderName) {
          return prev;
        }
        const next = { ...prev };
        delete next.cardholderName;
        return next;
      }
      if (prev.cardholderName === message) {
        return prev;
      }
      return { ...prev, cardholderName: message };
    });
  }, []);

  const handleCancel = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const handleApplyPromotionCode = useCallback(() => {
    resetIntentState();

    if (normalizedPromotionCode === appliedPromotionCode) {
      return;
    }

    setSummaryError(null);
    setAppliedPromotionCode(normalizedPromotionCode);
  }, [appliedPromotionCode, normalizedPromotionCode, resetIntentState]);

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    const phone = phoneNumber.trim();
    if (!phone) {
      errors.phoneNumber = '電話番号を入力してください。';
    } else if (!/^\d{10,11}$/.test(normalizeDigits(phone))) {
      errors.phoneNumber = '電話番号は10〜11桁の半角数字で入力してください。';
    }

    const postal = postalCode.trim();
    if (!postal) {
      errors.postalCode = '郵便番号を入力してください。';
    } else if (!/^\d{7}$/.test(normalizeDigits(postal))) {
      errors.postalCode = '郵便番号は7桁の半角数字で入力してください。';
    }

    if (!prefecture.trim()) {
      errors.prefecture = '都道府県を選択してください。';
    }
    if (!city.trim()) {
      errors.city = '市区町村を入力してください。';
    }
    if (!addressLine1.trim()) {
      errors.addressLine1 = '番地等を入力してください。';
    }

    return errors;
  }, [addressLine1, city, phoneNumber, postalCode, prefecture]);

  const handlePreparePayment = useCallback(() => {
    if (!selectedPriceId) {
      setFormError('プランを選択してください。');
      return;
    }
    if (replaceAtPeriodEnd && !isReplacementAccepted) {
      setFormError('現在の契約期間終了後に新しいプランへ切り替えることを確認してください。');
      return;
    }


    const errors = validateForm();
    setFieldErrors(errors);
    setTermsError(false);

    if (Object.keys(errors).length > 0) {
      setFormError('入力内容に不備があります。');
      return;
    }

    if (summaryError) {
      setFormError(summaryError);
      return;
    }

    setFormError(null);
    setStatusMessage(null);

    const normalizedPromotion = appliedPromotionCode || undefined;
    const billingName = organizationName.trim() || userName?.trim() || undefined;
    const billingDetails = buildBillingDetails(
      userEmail,
      billingName,
      normalizeDigits(phoneNumber.trim()),
      {
        line1: addressLine1.trim() || undefined,
        line2: addressLine2.trim() || undefined,
        city: city.trim() || undefined,
        state: prefecture.trim() || undefined,
        postalCode: normalizeDigits(postalCode.trim()) || undefined,
        country: 'JP',
      },
    );

    setPreparedPromotionCode(normalizedPromotion);
    setPreparedBillingDetails(billingDetails);
    setIsMethodSelectionVisible(true);
    setIntentInfo(null);
    setIsPreparing(false);
    setIsBankReviewVisible(false);
  }, [
    addressLine1,
    addressLine2,
    city,
    isReplacementAccepted,
    organizationName,
    phoneNumber,
    postalCode,
    prefecture,
    appliedPromotionCode,
    summaryError,
    replaceAtPeriodEnd,
    selectedPriceId,
    userEmail,
    userName,
    validateForm,
  ]);


  const handleSelectCardPayment = useCallback(async () => {
    if (!selectedPriceId) {
      setFormError('プランを選択してください。');
      return;
    }

    if (!preparedBillingDetails) {
      handlePreparePayment();
      return;
    }

    setTermsError(false);
    setIsPreparing(true);
    setFormError(null);
    setStatusMessage(null);

    try {
      if (replaceAtPeriodEnd) {
        const setupIntent = await createSetupIntent();
        if (!setupIntent.operationId) {
          throw new Error('決済処理IDを取得できませんでした。時間をおいて再試行してください。');
        }
        setIntentInfo({
          operationId: setupIntent.operationId,
          intentType: 'setup',
          subscriptionId: 'replacement-pending',
          clientSecret: setupIntent.clientSecret,
          status: 'requires_payment_method',
          summary,
        });
        setElementsKey((prev) => prev + 1);
        setIsMethodSelectionVisible(false);
        return;
      }

      const response = await createSubscriptionIntent(selectedPriceId, {
        promotionCode: preparedPromotionCode,
        billingDetails: preparedBillingDetails,
        userId: user?.id,
      });

      const nextSummary = mergeSummaryWithInvoice(
        {
          total: response.latestInvoiceTotal,
          subtotal: response.latestInvoiceSubtotal,
          tax: response.latestInvoiceTax,
          discount: response.latestInvoiceDiscount,
          dueDate: response.latestInvoiceDueDate,
        },
        summary,
      );
      setSummary(nextSummary);

      const operationId = response.operationId ?? '';
      if (!operationId) {
        const message = '決済処理IDを取得できませんでした。時間をおいて再試行してください。';
        setFormError(message);
        onError(message);
        return;
      }

      const subscriptionId = response.subscriptionId ?? '';
      if (!subscriptionId) {
        const message = 'サブスクリプションIDを特定できませんでした。時間をおいて再試行してください。';
        setFormError(message);
        onError(message);
        return;
      }

      const rawIntentType = response.intentType ?? 'payment';
      if (rawIntentType === 'none') {
        onSuccess({ operationId });
        setIsMethodSelectionVisible(false);
        return;
      }

      let statusMessageText = response.statusMessage ?? undefined;
      const resolvedPaymentMethodTypes =
        response.paymentMethodTypes && response.paymentMethodTypes.length > 0
          ? response.paymentMethodTypes
          : undefined;

      if (rawIntentType === 'invoice') {
        const invoiceStatus = response.paymentIntentStatus ?? response.subscriptionStatus ?? undefined;
        const invoiceMessage =
          statusMessageText ??
          (invoiceStatus === 'open'
            ? '銀行振込用の請求書を発行しました。以下のボタンから請求書を開き、案内に従ってお支払いください。'
            : undefined);

        const preparedInvoice: PreparedIntent = {
          operationId,
          intentType: 'invoice',
          subscriptionId,
          status: invoiceStatus,
          statusMessage: invoiceMessage ?? undefined,
          summary: nextSummary ?? null,
          paymentMethodTypes: resolvedPaymentMethodTypes,
          paymentMethodDomain: response.paymentMethodDomain,
          hostedInvoiceUrl: response.hostedInvoiceUrl,
          invoicePdfUrl: response.invoicePdfUrl,
        };

        setIntentInfo(preparedInvoice);
        setStatusMessage(null);
        setIsMethodSelectionVisible(false);
        return;
      }

      const intentType: PreparedIntent['intentType'] = rawIntentType === 'setup' ? 'setup' : 'payment';
      let clientSecret = response.clientSecret ?? undefined;
      let paymentStatus = response.paymentIntentStatus ?? undefined;

      const pollResumeIntent = async (): Promise<
        | {
            clientSecret?: string;
            paymentIntentStatus?: string;
            statusMessage?: string;
          }
        | { error: string }
      > => {
        const maxAttempts = 6;
        const delayMs = 1500;
        let lastStatusMessage = statusMessageText;

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }

          try {
            const pending = await resumeSubscriptionIntent(operationId);
            if (pending.statusMessage) {
              lastStatusMessage = pending.statusMessage;
              setStatusMessage(pending.statusMessage);
            }

            if (pending.clientSecret) {
              return {
                clientSecret: pending.clientSecret,
                paymentIntentStatus: pending.paymentIntentStatus ?? undefined,
                statusMessage: pending.statusMessage ?? lastStatusMessage,
              };
            }

            if (
              !pending.paymentIntentStatus
              || !PENDING_PAYMENT_INTENT_STATUSES.has(pending.paymentIntentStatus)
            ) {
              return {
                clientSecret: pending.clientSecret ?? undefined,
                paymentIntentStatus: pending.paymentIntentStatus ?? undefined,
                statusMessage: pending.statusMessage ?? lastStatusMessage,
              };
            }
          } catch (pendingError) {
            const message =
              pendingError instanceof Error
                ? pendingError.message
                : '決済状況の取得に失敗しました。';
            return { error: message };
          }
        }

        return {
          error: '決済情報の準備がタイムアウトしました。時間をおいて再試行してください。',
        };
      };

      if (intentType === 'payment' && !clientSecret) {
        const pendingResult = await pollResumeIntent();
        if ('error' in pendingResult) {
          setFormError(pendingResult.error);
          onError(pendingResult.error);
          return;
        }

        clientSecret = pendingResult.clientSecret ?? undefined;
        paymentStatus = pendingResult.paymentIntentStatus ?? paymentStatus;
        statusMessageText = pendingResult.statusMessage ?? statusMessageText;
      }

      if (!clientSecret) {
        const message = '決済の準備に失敗しました。時間をおいて再試行してください。';
        setFormError(message);
        onError(message);
        return;
      }

      if (intentType === 'payment' && clientSecret) {
        if (
          !paymentStatus
          || paymentStatus === 'requires_payment_method'
          || paymentStatus === 'requires_payment_intent'
        ) {
          statusMessageText = undefined;
        }
      }

      setStatusMessage(statusMessageText ?? null);

      const prepared: PreparedIntent = {
        intentType,
        subscriptionId,
        operationId,
        clientSecret,
        status: paymentStatus,
        statusMessage: statusMessageText,
        summary: nextSummary ?? null,
        paymentMethodTypes: resolvedPaymentMethodTypes,
        paymentMethodDomain: response.paymentMethodDomain,
      };

      setIntentInfo(prepared);
      setElementsKey((prev) => prev + 1);
      setIsMethodSelectionVisible(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'サブスクリプションの開始に失敗しました。時間をおいて再試行してください。';
      setFormError(message);
      onError(message);
    } finally {
      setIsPreparing(false);
    }
  }, [
    handlePreparePayment,
    onError,
    onSuccess,
    preparedBillingDetails,
    preparedPromotionCode,
    replaceAtPeriodEnd,
    selectedPriceId,
    summary,
    user?.id,
  ]);


  const handleCardSuccess = useCallback(
    async (result: PaymentConfirmationResult) => {
      if (!replaceAtPeriodEnd || intentInfo?.intentType !== 'setup') {
        onSuccess({ operationId: result.operationId });
        return;
      }

      const setupIntentId = result.setupIntentId;
      if (!setupIntentId || !preparedBillingDetails || !selectedPriceId) {
        const message = 'カード情報の確認結果を取得できませんでした。時間をおいて再試行してください。';
        setFormError(message);
        onError(message);
        setIsConfirming(false);
        return;
      }

      try {
        const response = await createSubscriptionIntent(selectedPriceId, {
          promotionCode: preparedPromotionCode,
          billingDetails: preparedBillingDetails,
          userId: user?.id,
          replaceAtPeriodEnd: true,
          setupIntentId,
        });
        const subscriptionId = response.subscriptionId;
        if (!subscriptionId || response.intentType !== 'schedule') {
          throw new Error('期末切替の予約結果を確認できませんでした。時間をおいて再試行してください。');
        }
        if (!response.operationId) {
          throw new Error('期末切替の決済処理IDを確認できませんでした。時間をおいて再試行してください。');
        }
        onSuccess({ operationId: response.operationId });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : '期末切替の予約に失敗しました。時間をおいて再試行してください。';
        setFormError(message);
        onError(message);
        setIsConfirming(false);
      }
    },
    [
      intentInfo?.intentType,
      onError,
      onSuccess,
      preparedBillingDetails,
      preparedPromotionCode,
      replaceAtPeriodEnd,
      selectedPriceId,
      user?.id,
    ],
  );
  const handleShowBankReview = useCallback(() => {
    if (!selectedPriceId) {
      setFormError('プランを選択してください。');
      return;
    }

    if (!preparedBillingDetails) {
      handlePreparePayment();
      return;
    }

    setFormError(null);
    setStatusMessage(null);
    setTermsError(false);
    setIsBankReviewVisible(true);
  }, [handlePreparePayment, preparedBillingDetails, selectedPriceId]);
  const handleConfirmBankTransfer = useCallback(async () => {
    if (!selectedPriceId) {
      setFormError('プランを選択してください。');
      return;
    }

    if (!preparedBillingDetails) {
      handlePreparePayment();
      setIsBankReviewVisible(false);
      setTermsError(false);
      return;
    }

    if (!isTermsAccepted) {
      setTermsError(true);
      return;
    }

    setTermsError(false);
    setIsPreparing(true);
    setFormError(null);
    setStatusMessage(null);

    try {
      const response = await createBankTransferInvoice(selectedPriceId, {
        promotionCode: preparedPromotionCode,
        billingDetails: preparedBillingDetails,
        userId: user?.id,
        replaceAtPeriodEnd,
      });

      const nextSummary = mergeSummaryWithInvoice(
        {
          total: response.latestInvoiceTotal,
          subtotal: response.latestInvoiceSubtotal,
          tax: response.latestInvoiceTax,
          discount: response.latestInvoiceDiscount,
          dueDate: response.latestInvoiceDueDate,
        },
        summary,
      );
      setSummary(nextSummary);

      const operationId = response.operationId ?? '';
      if (!operationId) {
        const message = '決済処理IDを取得できませんでした。時間をおいて再試行してください。';
        setFormError(message);
        onError(message);
        return;
      }

      const subscriptionId = response.subscriptionId ?? '';
      if (!subscriptionId) {
        const message = 'サブスクリプションIDを特定できませんでした。時間をおいて再試行してください。';
        setFormError(message);
        onError(message);
        return;
      }
      if (response.intentType === 'schedule') {
        setIsBankReviewVisible(false);
        onSuccess({ operationId });
        return;
      }


      const invoiceStatus = response.paymentIntentStatus ?? response.subscriptionStatus ?? undefined;
      const invoiceMessage =
        response.statusMessage ??
        (invoiceStatus === 'open'
          ? '銀行振込用の請求書を発行しました。以下のボタンから請求書を開き、案内に従ってお支払いください。'
          : undefined);

      const preparedInvoice: PreparedIntent = {
        operationId,
        intentType: 'invoice',
        subscriptionId,
        status: invoiceStatus,
        statusMessage: invoiceMessage ?? undefined,
        summary: nextSummary ?? null,
        paymentMethodTypes: response.paymentMethodTypes ?? undefined,
        paymentMethodDomain: response.paymentMethodDomain,
        hostedInvoiceUrl: response.hostedInvoiceUrl,
        invoicePdfUrl: response.invoicePdfUrl,
      };

      setIntentInfo(preparedInvoice);
      setIsMethodSelectionVisible(false);
      setIsBankReviewVisible(false);
      setTermsError(false);
      setStatusMessage(null);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : '請求書の発行に失敗しました。時間をおいて再試行してください。';
      setFormError(message);
      onError(message);
    } finally {
      setIsPreparing(false);
    }
  }, [
    handlePreparePayment,
    isTermsAccepted,
    onError,
    onSuccess,
    preparedBillingDetails,
    preparedPromotionCode,
    replaceAtPeriodEnd,
    selectedPriceId,
    summary,
    user?.id,
  ]);
  const appearance = useMemo(
    () => ({
      theme: 'stripe',
      variables: {
        colorPrimary: '#1976d2',
        colorText: '#1a202c',
        fontFamily: '"Noto Sans JP", sans-serif',
      },
    }),
    [],
  );

  const billingDetailsForStripe = useMemo(() => {
    const resolvedCardholderName = cardholderName.trim() || undefined;
    return buildBillingDetails(
      userEmail,
      resolvedCardholderName,
      normalizeDigits(phoneNumber.trim()) || undefined,
      {
        line1: addressLine1.trim() || undefined,
        line2: addressLine2.trim() || undefined,
        city: city.trim() || undefined,
        state: prefecture.trim() || undefined,
        postalCode: normalizeDigits(postalCode.trim()) || undefined,
        country: 'JP',
      },
    );
  }, [
    addressLine1,
    addressLine2,
    cardholderName,
    city,
    phoneNumber,
    postalCode,
    prefecture,
    userEmail,
  ]);

  const handleCardReviewVisibilityChange = useCallback(
    (visible: boolean) => {
      setIsCardReviewVisible(visible);
    },
    [setIsCardReviewVisible],
  );

  const renderSummary = ({ showBreakdown = true }: { showBreakdown?: boolean } = {}) => {
    if (isLoadingSummary) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">金額を計算しています…</Typography>
        </Box>
      );
    }

    if (summaryError) {
      return <Alert severity="warning">{summaryError}</Alert>;
    }

    if (!summary) {
      return null;
    }

    const showTotal = typeof summary.total === 'number';
    const subtotalLabel = formatYen(summary.subtotal);
    const taxLabel = formatYen(summary.tax);
    const discountLabel = formatYen(summary.discount);
    const showSubtotal = showBreakdown && Boolean(subtotalLabel);
    const showTax = showBreakdown && Boolean(taxLabel);
    const showDiscount =
      showBreakdown && typeof summary.discount === 'number' && summary.discount > 0;

    return (
      <Stack spacing={showBreakdown ? 0.5 : 1}>
        {showSubtotal && (
          <Typography variant="body1">小計: {subtotalLabel}</Typography>
        )}
        {showDiscount && (
          <Typography variant="body1">割引: -{discountLabel}</Typography>
        )}
        {showTax && <Typography variant="body1">税額: {taxLabel}</Typography>}
        {showTotal && (
          <Typography variant="h5" fontWeight="bold">
            合計: {formatYen(summary.total)}
          </Typography>
        )}
      </Stack>
    );
  };

  const showMethodSelection = isMethodSelectionVisible && !intentInfo && !isBankReviewVisible;
  const showSecondaryStep = showMethodSelection || Boolean(intentInfo) || isBankReviewVisible;
  const showStripePaymentStep =
    intentInfo?.intentType === 'payment' || intentInfo?.intentType === 'setup';
  const showInvoiceStep = intentInfo?.intentType === 'invoice';
  const cardPaymentButtonLabel = isApplePayAvailable
    ? 'クレジットカード・Apple Pay・Linkで支払う'
    : 'クレジットカード・Linkで支払う';

  useEffect(() => {
    if (!showStripePaymentStep) {
      setIsCardReviewVisible(false);
    }
  }, [showStripePaymentStep]);

  useEffect(() => {
    onFinalReviewVisibilityChange?.(isBankReviewVisible || isCardReviewVisible);
  }, [isBankReviewVisible, isCardReviewVisible, onFinalReviewVisibilityChange]);

  useEffect(
    () => () => {
      onFinalReviewVisibilityChange?.(false);
    },
    [onFinalReviewVisibilityChange],
  );

  if (!stripePromise) {
    return null;
  }

  return (
    <Box>
      {formError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {formError}
        </Alert>
      )}
      {!showSecondaryStep && (
        <Box component="form" onSubmit={(event) => { event.preventDefault(); void handlePreparePayment(); }} sx={{ mt: 3 }}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                プランを選択
              </Typography>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                  value={selectedPriceId}
                  onChange={(event) => {
                    resetIntentState();
                    setSelectedPriceId(event.target.value);
                  }}
                  sx={{
                    display: 'grid',
                    gap: 2.5,
                    width: '100%',
                    justifyItems: 'center',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, minmax(0, 1fr))',
                    },
                  }}
                >
                  {priceOptions.map((option) => {
                    const monthlyEquivalentLabel = option.monthlyEquivalent ? formatYen(option.monthlyEquivalent) : undefined;
                    return (
                      <FormControlLabel
                        key={option.id}
                        value={option.id}
                        control={<Radio />}
                        label={
                          <Stack spacing={0.5} alignItems="flex-start" textAlign="left">
                            <Typography variant="subtitle1" fontWeight="bold">
                              {option.label}
                            </Typography>
                            {option.description && (
                              <Typography variant="body2" color="text.secondary">
                                {option.description}
                              </Typography>
                            )}
                            {option.priceDisplay && (
                              <Typography variant="body2" color="text.secondary">
                                {option.priceDisplay}
                              </Typography>
                            )}
                            {monthlyEquivalentLabel && (
                              <Typography variant="caption" color="text.secondary">
                                1ヶ月あたり {monthlyEquivalentLabel}
                              </Typography>
                            )}
                          </Stack>
                        }
                        sx={{
                          m: 0,
                          py: 2.5,
                          px: 3.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: option.id === selectedPriceId ? 'primary.main' : 'grey.300',
                          bgcolor: 'common.white',
                          width: '100%',
                          maxWidth: { xs: '100%', sm: 440, md: 520 },
                          boxShadow: option.id === selectedPriceId ? 3 : 0,
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                        }}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </Box>

            <Stack spacing={2}>
              <Typography variant="h6">請求先情報</Typography>
              <TextField
                label="メールアドレス"
                value={userEmail ?? ''}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="アカウントに登録されているメールアドレスです"
              />
              <TextField
                label="病院名または代表者様氏名（任意）"
                value={organizationName}
                onChange={(event) => {
                  resetIntentState();
                  if (!hasOrganizationNameBeenEdited) {
                    setHasOrganizationNameBeenEdited(true);
                  }
                  setOrganizationName(event.target.value);
                }}
                fullWidth
                helperText="空欄の場合はアカウント名が使用されます"
              />
              <TextField
                label="電話番号"
                value={phoneNumber}
                onChange={(event) => {
                  resetIntentState();
                  setPhoneNumber(event.target.value.replace(/[^0-9]/g, ''));
                }}
                fullWidth
                error={Boolean(fieldErrors.phoneNumber)}
                helperText={fieldErrors.phoneNumber ?? '半角数字のみで入力してください (例: 0312345678)'}
                inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
              />
            </Stack>

            <Stack spacing={2}>
              <Typography variant="h6">請求先住所</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  alignItems: 'flex-start',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <TextField
                  label="郵便番号"
                  value={postalCode}
                  onChange={(event) => {
                    resetIntentState();
                    const digits = event.target.value.replace(/[^0-9]/g, '').slice(0, 7);
                    setPostalCode(digits);
                    setPostalLookupMessage(null);
                  }}
                  fullWidth
                  error={Boolean(fieldErrors.postalCode)}
                  helperText={
                    fieldErrors.postalCode ? (
                      fieldErrors.postalCode
                    ) : (
                      <>
                        例: 1000001
                        {postalLookupMessage ? (
                          <>
                            <br />
                            {postalLookupMessage}
                          </>
                        ) : null}
                      </>
                    )
                  }
                  inputProps={{ inputMode: 'numeric', pattern: '\\d*', maxLength: 7 }}
                  InputProps={
                    isPostalLookupLoading
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <CircularProgress size={16} aria-label="住所検索中" />
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
                <TextField
                  select
                  label="都道府県"
                  value={prefecture}
                  onChange={(event) => {
                    resetIntentState();
                    setManualAddressOverrides((prev) => ({ ...prev, prefecture: true }));
                    setPrefecture(event.target.value);
                  }}
                  fullWidth
                  error={Boolean(fieldErrors.prefecture)}
                  helperText={fieldErrors.prefecture}
                  SelectProps={{
                    sx: {
                      textAlign: 'left',
                    },
                  }}
                >
                  {prefectures.map((pref) => (
                    <MenuItem key={pref} value={pref}>
                      {pref}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                label="市区町村"
                value={city}
                onChange={(event) => {
                  resetIntentState();
                  setManualAddressOverrides((prev) => ({ ...prev, city: true }));
                  setCity(event.target.value);
                }}
                fullWidth
                error={Boolean(fieldErrors.city)}
                helperText={fieldErrors.city}
              />
              <TextField
                label="番地等"
                value={addressLine1}
                onChange={(event) => {
                  resetIntentState();
                  setManualAddressOverrides((prev) => ({ ...prev, addressLine1: true }));
                  setAddressLine1(event.target.value);
                }}
                fullWidth
                error={Boolean(fieldErrors.addressLine1)}
                helperText={fieldErrors.addressLine1}
              />
              <TextField
                label="建物名・部屋番号 (任意)"
                value={addressLine2}
                onChange={(event) => {
                  resetIntentState();
                  setAddressLine2(event.target.value);
                }}
                fullWidth
              />
            </Stack>

            <Stack spacing={1}>
              {!isPromotionCodeFieldVisible ? (
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    setIsPromotionCodeFieldVisible(true);
                  }}
                  variant="body2"
                  sx={{ alignSelf: 'flex-start', fontSize: '0.875rem' }}
                >
                  プロモーションコードを利用する
                </Link>
              ) : (
                <TextField
                  label="プロモーションコード"
                  value={promotionCode}
                  onChange={(event) => {
                    resetIntentState();
                    setPromotionCode(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') {
                      return;
                    }
                    event.preventDefault();
                    handleApplyPromotionCode();
                  }}
                  fullWidth
                  placeholder="例: SPRING2025"
                  helperText="コード入力後に Enter キーで適用します"
                />
              )}
            </Stack>

            {replaceAtPeriodEnd && (
              <Alert severity="info">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={isReplacementAccepted}
                      onChange={(event) => setIsReplacementAccepted(event.target.checked)}
                    />
                  )}
                  label="現在の契約期間終了後に新しいプランへ切り替える"
                />
                現在の契約は新プランの予約成功後にのみ期末解約となり、重複課金は行われません。
              </Alert>
            )}

            <Divider />

            <Stack spacing={1}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                お支払い金額
              </Typography>
              {renderSummary({ showBreakdown: true })}
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              alignItems="center"
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            >
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                sx={{ minWidth: 130, py: 1.25 }}
              >
                キャンセル
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPreparing}
                sx={{ minWidth: 190, py: 1.25 }}
              >
                {isPreparing ? '処理中…' : '決済情報の入力へ'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {showMethodSelection && (
        <Box sx={{ mt: 3 }}>
          {(summary || isLoadingSummary || summaryError) && !isCardReviewVisible && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                お支払い金額
              </Typography>
              {renderSummary({ showBreakdown: true })}
            </Box>
          )}
          <Typography variant="h6" sx={{ mb: 2 }}>
            お支払い方法を選択
          </Typography>
          <Stack spacing={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={isPreparing}
              sx={{ textTransform: 'none' }}
              onClick={() => { void handleSelectCardPayment(); }}
            >
              {cardPaymentButtonLabel}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              disabled={isPreparing}
              onClick={handleShowBankReview}
            >
              銀行振込（請求書を発行）
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => { setIsMethodSelectionVisible(false); }}
              sx={{ alignSelf: 'flex-start' }}
            >
              前のページに戻る
            </Button>
          </Stack>
        </Box>
      )}

      {isBankReviewVisible && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            契約内容最終確認
          </Typography>
          <FinalConfirmationDetails
            planLabel={selectedPlan?.label}
            paymentMethodLabel="銀行振込"
            summary={summary}
            summaryError={summaryError}
            isLoadingSummary={isLoadingSummary}
            showDueDate
            nextPeriodEnd={
              typeof subscriptionPeriodEnd === 'number' ? subscriptionPeriodEnd : undefined
            }
            planInterval={selectedPlan?.billingInterval}
          />
          <Stack spacing={2} sx={{ mt: 3 }}>
            <TermsAgreementSection
              checked={isTermsAccepted}
              onChange={handleTermsChange}
              showError={termsError}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                width: '100%',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: { sm: 'space-between' },
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setIsBankReviewVisible(false);
                  setTermsError(false);
                }}
                disabled={isPreparing}
                sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
              >
                前のページに戻る
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  void handleConfirmBankTransfer();
                }}
                disabled={isPreparing}
                sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
              >
                {isPreparing ? '処理中…' : '申込みを確定する（請求書発行）'}
              </Button>
            </Box>
          </Stack>
        </Box>
      )}

        {showStripePaymentStep && intentInfo?.clientSecret && stripeReady && stripePromise && (
          <Elements
            key={elementsKey}
            stripe={stripePromise}
            options={{ clientSecret: intentInfo.clientSecret, appearance } as StripeElementsOptions}
          >
            {(summary || isLoadingSummary || summaryError) && !isCardReviewVisible && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  お支払い金額
                </Typography>
                {renderSummary()}
              </Box>
            )}
            <PaymentConfirmationStep
              intent={{ ...intentInfo, statusMessage: statusMessage ?? intentInfo.statusMessage }}
              billingDetails={billingDetailsForStripe}
              cardholderName={cardholderName}
              onCardholderNameChange={handleCardholderNameChange}
              cardholderError={fieldErrors.cardholderName}
              onCardholderErrorChange={handleCardholderErrorChange}
              onBack={backToMethodSelection}
              onSuccess={handleCardSuccess}
              onError={onError}
              setFormError={setFormError}
              isConfirming={isConfirming}
              setIsConfirming={setIsConfirming}
              summary={summary}
              summaryError={summaryError}
              isLoadingSummary={isLoadingSummary}
              planLabel={selectedPlan?.label}
              planInterval={selectedPlan?.billingInterval}
              onReviewVisibilityChange={handleCardReviewVisibilityChange}
              applePayStatus={applePayStatus}
              isTermsAccepted={isTermsAccepted}
              onTermsChange={handleTermsChange}
              termsError={termsError}
              onTermsErrorChange={setTermsError}
              subscriptionPeriodEnd={subscriptionPeriodEnd ?? null}
            />
        </Elements>
      )}
      {showInvoiceStep && intentInfo && (
        <Box sx={{ mt: 3 }}>
          {(summary || isLoadingSummary || summaryError) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                お支払い金額
              </Typography>
              {renderSummary()}
            </Box>
          )}
          <InvoicePaymentStep intent={intentInfo} onBack={backToMethodSelection} />
        </Box>
      )}
    </Box>
  );
};

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const isViewer = Boolean(user?.isViewer);
  const isPeriodEndCancellationScheduled = Boolean(
    user?.hasActiveSubscription
    && user.subscriptionCancelAtPeriodEnd === true
  );
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const stripeConfig = useMemo(
    () =>
      resolveStripePublishableKey(
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
        process.env.REACT_APP_STRIPE_MODE,
      ),
    [],
  );
  const stripePromise = useMemo(() => (stripeConfig.key ? loadStripe(stripeConfig.key) : null), [stripeConfig.key]);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(false);

  const [priceOptions, setPriceOptions] = useState<PriceOption[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [isFinalReviewVisible, setIsFinalReviewVisible] = useState(false);
  const applePayStatus = useApplePayStatus();

  useEffect(() => {
    let isActive = true;

    if (!stripePromise) {
      setStripeInstance(null);
      setStripeReady(false);
      setStripeError(null);
      setIsStripeLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsStripeLoading(true);
    setStripeError(null);
    setStripeReady(false);

    const resolveStripe = async () => {
      try {
        const resolvedStripe = await stripePromise;
        if (!isActive) {
          return;
        }
        if (!resolvedStripe) {
          setStripeInstance(null);
          setStripeReady(false);
          setStripeError(STRIPE_SCRIPT_BLOCKED_MESSAGE);
          return;
        }
        setStripeInstance(resolvedStripe);
        setStripeReady(true);
      } catch {
        if (!isActive) {
          return;
        }
        setStripeInstance(null);
        setStripeReady(false);
        setStripeError(STRIPE_INITIALIZATION_FAILED_MESSAGE);
      } finally {
        if (isActive) {
          setIsStripeLoading(false);
        }
      }
    };

    void resolveStripe();

    return () => {
      isActive = false;
    };
  }, [stripePromise]);

  useEffect(() => {
    let isCancelled = false;

    const loadPrices = async () => {
      setIsLoadingPrices(true);
      setPriceError(null);
      try {
        const prices = await fetchSubscriptionPrices();
        if (isCancelled) {
          return;
        }
        setPriceOptions(mapPricesToOptions(prices));
      } catch (error) {
        if (isCancelled) {
          return;
        }
        const message =
          error instanceof Error ? error.message : '価格情報を取得できませんでした。時間をおいて再試行してください。';
        setPriceOptions([]);
        setPriceError(message);
      } finally {
        if (!isCancelled) {
          setIsLoadingPrices(false);
        }
      }
    };

    if (!isViewer) {
      void loadPrices();
    }

    return () => {
      isCancelled = true;
    };
  }, [isViewer, reloadToken]);

  const handleReloadPrices = useCallback(() => {
    setReloadToken((prev) => prev + 1);
  }, []);

  const handleOpenPortal = useCallback(async () => {
    setIsOpeningPortal(true);
    try {
      const portalUrl = await createCustomerPortalSession();
      window.location.assign(portalUrl);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : '契約管理画面を開けませんでした。時間をおいて再度お試しください。';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsOpeningPortal(false);
    }
  }, []);


  const handleSuccess = useCallback(
    (payload: SubscriptionSuccessPayload) => {
      setSnackbar({ open: true, message: '決済結果を確認しています。', severity: 'info' });
      window.setTimeout(() => {
        navigate(`/subscription/success?operation_id=${encodeURIComponent(payload.operationId)}`);
      }, 300);
    },
    [navigate],
  );

  const handleError = useCallback((message: string) => {
    setSnackbar({ open: true, message, severity: 'error' });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  if (isViewer) {
    return <Navigate to="/" replace />;
  }

  if (user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (user?.isVip) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {!isFinalReviewVisible && (
          <>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              サブスクリプション購入
            </Typography>
            <Typography variant="subtitle1" align="center" sx={{ mb: 5 }}>
              手指衛生データ分析アプリのご利用にはサブスクリプションが必要です
            </Typography>
          </>
        )}

        {user?.hasActiveSubscription && (
          <Box sx={{ textAlign: 'center', my: 3 }}>
            {isPeriodEndCancellationScheduled ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                このサブスクリプションは現在の契約期間終了時に解約されます。
                終了日までは利用でき、次回の自動更新はありません。
              </Alert>
            ) : (
              <Typography variant="body1" sx={{ mb: 2 }}>
                現在の契約を維持したまま、契約期間終了後のプラン切替を予約できます。
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={() => { void handleOpenPortal(); }}
              disabled={isOpeningPortal}
            >
              {isOpeningPortal ? '契約管理画面を準備中…' : 'サブスクリプション管理へ'}
            </Button>
          </Box>
        )}

        {isAuthenticated && !isPeriodEndCancellationScheduled && (
          <Box sx={{ textAlign: 'center', my: 3 }}>
            {!stripeConfig.key || !stripePromise ? (
              <Alert severity="error">{stripeConfig.error ?? STRIPE_PUBLISHABLE_KEY_MISSING_MESSAGE}</Alert>
            ) : !stripeReady ? (
              isStripeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress aria-label="決済フォームを初期化中" />
                </Box>
              ) : (
                <Alert severity="error">
                  {stripeError ?? STRIPE_INITIALIZATION_FAILED_MESSAGE}
                </Alert>
              )
            ) : priceError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={handleReloadPrices}>
                    再取得
                  </Button>
                }
              >
                {priceError}
              </Alert>
            ) : isLoadingPrices ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress aria-label="価格情報を読み込み中" />
              </Box>
            ) : priceOptions.length === 0 ? (
              <Alert severity="warning">利用可能なプランが設定されていません。管理者にお問い合わせください。</Alert>
            ) : (
              <SubscriptionForm
                priceOptions={priceOptions}
                stripePromise={stripePromise}
                stripeReady={stripeReady && Boolean(stripeInstance)}
                userEmail={user?.email}
                userName={user?.username}
                userHospitalName={user?.hospitalName}
                onSuccess={handleSuccess}
                onError={handleError}
                onFinalReviewVisibilityChange={setIsFinalReviewVisible}
                applePayStatus={applePayStatus}
                subscriptionPeriodEnd={user?.subscriptionPeriodEnd ?? null}
                replaceAtPeriodEnd={Boolean(user?.hasActiveSubscription)}
              />
            )}
          </Box>
        )}

        {!isAuthenticated && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
            <Button variant="contained" color="primary" component={RouterLink} to="/register">
              新規登録
            </Button>
            <Button variant="outlined" color="primary" component={RouterLink} to="/login">
              ログイン
            </Button>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Subscription;
